import { useHttp } from "../http/useHttp";
import {
  DATE_FORMAT_REPORT_FILENAME_DEFAULT,
  dayjs,
  makeFilename,
} from "@synota-io/synota-shared-ui";
import { useAxiosQuery, UseAxiosQueryOptions } from "../http/useAxiosQuery";
import { paths } from "../generated-types";

type InvoicesReportOperation =
  paths["/dashboard/contracts/{contract_shared_uuid}/invoices/reports/{format}"]["post"];
type InvoicesReportRequestBody =
  InvoicesReportOperation["requestBody"]["content"]["application/json"];
type InvoicesReportPath = InvoicesReportOperation["parameters"]["path"];

export type UseInvoicesReportInput = InvoicesReportRequestBody & {
  contract_uuid: InvoicesReportPath["contract_shared_uuid"];
};
export interface UseInvoicesReportMeta {
  contract_name: string;
}

export const useInvoicesReport = (
  input: UseInvoicesReportInput,
  meta: UseInvoicesReportMeta = { contract_name: "Contract" },
  options: Omit<UseAxiosQueryOptions<Blob>, "queryKey" | "queryFn"> = {},
) => {
  const { client } = useHttp();

  const {
    data,
    error,
    isFetching: isPending,
    status,
    refetch,
  } = useAxiosQuery<Blob>({
    queryKey: ["contract-invoices-report-csv", input],
    queryFn: () =>
      client.post(`/contracts/${input.contract_uuid}/invoices/reports/csv`, input, {
        responseType: "blob",
      }),
    ...options,
  });

  const isAvailable = status === "success" && !!data.data && data.data.size > 0;

  const download = () => {
    if (data && data.data.size > 0) {
      const blob = data.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        makeFilename(
          meta.contract_name,
          "Invoices",
          dayjs(input.energy_date_start).format(DATE_FORMAT_REPORT_FILENAME_DEFAULT),
          input.energy_date_end
            ? dayjs(input.energy_date_end).format(DATE_FORMAT_REPORT_FILENAME_DEFAULT)
            : undefined,
        )("csv"),
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } else {
      refetch().then((res) => {
        if (res.data?.data?.size && res.data.data.size > 0) {
          const blob = res.data.data;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            makeFilename(
              meta.contract_name,
              "Invoices",
              dayjs(input.energy_date_start).format(DATE_FORMAT_REPORT_FILENAME_DEFAULT),
              input.energy_date_end
                ? dayjs(input.energy_date_end).format(DATE_FORMAT_REPORT_FILENAME_DEFAULT)
                : undefined,
            )("csv"),
          );
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        }
      });
    }
  };

  return {
    download,
    error,
    isPending,
    status,
    isAvailable,
  };
};

export type UseInvoicesReportReturn = ReturnType<typeof useInvoicesReport>;
